<template>
  <section
    class="section is-main-section no-padding-top report-summary-section enable-select-text"
  >
    <!-- New structure with sheets and tables -->
    <div v-if="useSheetConfig" class="table-container">
      <!-- Outer tabs for sheets -->
      <b-tabs
        v-if="sheetConfig.length > 1"
        v-model="activeSheetTab"
        type="is-toggle"
        class="sheet-tabs"
      >
        <b-tab-item
          v-for="(sheet, sheetIndex) in sheetConfig"
          :key="`sheet-${sheetIndex}`"
          :label="
            sheet.sheet_name ||
              sheet.name ||
              sheet.sheet_json?.name ||
              `Sheet ${sheetIndex + 1}`
          "
        >
          <!-- Inner tabs for tables within the sheet -->
          <b-tabs
            v-if="getSheetTables(sheet).length > 1"
            v-model="activeTab"
            type="is-boxed"
            class="table-tabs"
          >
            <b-tab-item
              v-for="(table, tableIndex) in getSheetTables(sheet)"
              :key="`table-${sheetIndex}-${tableIndex}`"
              :label="table.table_name || table.table_json?.tableName"
            >
              <div class="table-responsive">
                <ReportSummaryHeader
                  :data="data"
                  :uplift-applied="isUpliftApplied"
                  :uplift-type="localData.uplift_type"
                  :td-without-pd="TD_WITHOUT_PD"
                />
                <table class="table is-striped is-hoverable is-fullwidth">
                  <thead>
                    <tr>
                      <th
                        :rowspan="
                          getHeaderDepth(getTableData(table).headers) + 2
                        "
                      >
                        Category / Subcategory
                      </th>
                      <th
                        v-for="(header, headerIndex) in getTableData(table)
                          .headers"
                        :key="`head-${headerIndex}`"
                        :colspan="getHeaderColspan(header)"
                        class="text-center"
                      >
                        {{ header.name }}
                      </th>
                    </tr>
                    <tr
                      v-for="level in getHeaderDepth(
                        getTableData(table).headers
                      )"
                      :key="`level-${level}`"
                    >
                      <th
                        v-for="cell in buildHeaderLevel(
                          getTableData(table).headers,
                          level
                        )"
                        :key="cell.key"
                        :colspan="cell.colspan"
                        class="text-center"
                      >
                        {{ cell.label }}
                      </th>
                    </tr>
                    <tr>
                      <th
                        v-for="cell in buildUnitsWeightHeaderCells(
                          getTableData(table).headers
                        )"
                        :key="cell.key"
                        class="text-center"
                      >
                        {{ cell.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in buildRows(
                        getTableData(table).headers,
                        getTableData(table).categories
                      )"
                      :key="row.key"
                      :class="row.className"
                    >
                      <td :class="row.indent ? 'pl-5' : 'has-text-weight-bold'">
                        {{ row.name }}
                      </td>
                      <td
                        v-for="cell in row.cells"
                        :key="cell.key"
                        :class="cell.className"
                      >
                        {{ formatCellValue(cell.value) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <CategoryListTable
                  :data="data"
                  :uplift-applied="isUpliftApplied"
                  :uplift-type="localData.uplift_type"
                  :td-without-pd="TD_WITHOUT_PD"
                />
              </div>
            </b-tab-item>
          </b-tabs>
          <!-- Single table in sheet -->
          <div v-else>
            <div class="table-responsive">
              <ReportSummaryHeader
                :data="data"
                :uplift-applied="isUpliftApplied"
                :uplift-type="localData.uplift_type"
                :td-without-pd="TD_WITHOUT_PD"
              />
              <table class="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th
                      :rowspan="
                        getHeaderDepth(
                          getTableData(getSheetTables(sheet)[0]).headers
                        ) + 2
                      "
                    >
                      Category / Subcategory
                    </th>
                    <th
                      v-for="(header, headerIndex) in getTableData(
                        getSheetTables(sheet)[0]
                      ).headers"
                      :key="`head-${sheetIndex}-0-${headerIndex}`"
                      :colspan="getHeaderColspan(header)"
                      class="text-center"
                    >
                      {{ header.name }}
                    </th>
                  </tr>
                  <tr
                    v-for="level in getHeaderDepth(
                      getTableData(getSheetTables(sheet)[0]).headers
                    )"
                    :key="`level-${sheetIndex}-0-${level}`"
                  >
                    <th
                      v-for="cell in buildHeaderLevel(
                        getTableData(getSheetTables(sheet)[0]).headers,
                        level
                      )"
                      :key="cell.key"
                      :colspan="cell.colspan"
                      class="text-center"
                    >
                      {{ cell.label }}
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="cell in buildUnitsWeightHeaderCells(
                        getTableData(getSheetTables(sheet)[0]).headers
                      )"
                      :key="cell.key"
                      class="text-center"
                    >
                      {{ cell.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in buildRows(
                      getTableData(getSheetTables(sheet)[0]).headers,
                      getTableData(getSheetTables(sheet)[0]).categories
                    )"
                    :key="row.key"
                    :class="row.className"
                  >
                    <td :class="row.indent ? 'pl-5' : 'has-text-weight-bold'">
                      {{ row.name }}
                    </td>
                    <td
                      v-for="cell in row.cells"
                      :key="cell.key"
                      :class="cell.className + ' text-center'"
                    >
                      {{ formatCellValue(cell.value) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <CategoryListTable
                :data="data"
                :uplift-applied="isUpliftApplied"
                :uplift-type="localData.uplift_type"
                :td-without-pd="TD_WITHOUT_PD"
              />
            </div>
          </div>
        </b-tab-item>
      </b-tabs>

      <!-- Single sheet - no outer tabs -->
      <div v-else>
        <h3 class="table-title">
          {{
            sheetConfig[0].sheet_name ||
              sheetConfig[0].name ||
              sheetConfig[0].sheet_json?.name ||
              "Sheet 1"
          }}
        </h3>
        <!-- Inner tabs for multiple tables in single sheet -->
        <b-tabs
          v-if="getSheetTables(sheetConfig[0]).length > 1"
          v-model="activeTab"
          type="is-boxed"
          class="table-tabs"
        >
          <b-tab-item
            v-for="(table, tableIndex) in getSheetTables(sheetConfig[0])"
            :key="`table-0-${tableIndex}`"
            :label="table.table_name || table.table_json?.tableName"
          >
            <div class="table-responsive">
              <ReportSummaryHeader
                :data="data"
                :uplift-applied="isUpliftApplied"
                :uplift-type="localData.uplift_type"
                :td-without-pd="TD_WITHOUT_PD"
              />
              <table class="table is-striped is-hoverable is-fullwidth">
                <thead>
                  <tr>
                    <th
                      :rowspan="getHeaderDepth(getTableData(table).headers) + 2"
                    >
                      Category / Subcategory
                    </th>
                    <th
                      v-for="(header, headerIndex) in getTableData(table)
                        .headers"
                      :key="`head-${headerIndex}`"
                      :colspan="getHeaderColspan(header)"
                      class="text-center"
                    >
                      {{ header.name }}
                    </th>
                  </tr>
                  <tr
                    v-for="level in getHeaderDepth(getTableData(table).headers)"
                    :key="`level-${level}`"
                  >
                    <th
                      v-for="cell in buildHeaderLevel(
                        getTableData(table).headers,
                        level
                      )"
                      :key="cell.key"
                      :colspan="cell.colspan"
                      class="text-center"
                    >
                      {{ cell.label }}
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="cell in buildUnitsWeightHeaderCells(
                        getTableData(table).headers
                      )"
                      :key="cell.key"
                      class="text-center"
                    >
                      {{ cell.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in buildRows(
                      getTableData(table).headers,
                      getTableData(table).categories
                    )"
                    :key="row.key"
                    :class="row.className"
                  >
                    <td :class="row.indent ? 'pl-5' : 'has-text-weight-bold'">
                      {{ row.name }}
                    </td>
                    <td
                      v-for="cell in row.cells"
                      :key="cell.key"
                      :class="cell.className"
                    >
                      {{ formatCellValue(cell.value) }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <CategoryListTable
                :data="data"
                :uplift-applied="isUpliftApplied"
                :uplift-type="localData.uplift_type"
                :td-without-pd="TD_WITHOUT_PD"
              />
            </div>
          </b-tab-item>
        </b-tabs>
        <!-- Single table in single sheet -->
        <div v-else>
          <div class="table-responsive">
            <ReportSummaryHeader
              :data="data"
              :uplift-applied="isUpliftApplied"
              :uplift-type="localData.uplift_type"
              :td-without-pd="TD_WITHOUT_PD"
            />
            <table class="table is-striped is-hoverable is-fullwidth">
              <thead>
                <tr>
                  <th
                    :rowspan="
                      getHeaderDepth(
                        getTableData(getSheetTables(sheetConfig[0])[0]).headers
                      ) + 2
                    "
                  >
                    Category / Subcategory
                  </th>
                  <th
                    v-for="(header, headerIndex) in getTableData(
                      getSheetTables(sheetConfig[0])[0]
                    ).headers"
                    :key="`head0-${headerIndex}`"
                    :colspan="getHeaderColspan(header)"
                    class="text-center"
                  >
                    {{ header.name }}
                  </th>
                </tr>
                <tr
                  v-for="level in getHeaderDepth(
                    getTableData(getSheetTables(sheetConfig[0])[0]).headers
                  )"
                  :key="`level-0-0-${level}`"
                >
                  <th
                    v-for="cell in buildHeaderLevel(
                      getTableData(getSheetTables(sheetConfig[0])[0]).headers,
                      level
                    )"
                    :key="cell.key"
                    :colspan="cell.colspan"
                    class="text-center"
                  >
                    {{ cell.label }}
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="cell in buildUnitsWeightHeaderCells(
                      getTableData(getSheetTables(sheetConfig[0])[0]).headers
                    )"
                    :key="cell.key"
                    class="text-center"
                  >
                    {{ cell.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in buildRows(
                    getTableData(getSheetTables(sheetConfig[0])[0]).headers,
                    getTableData(getSheetTables(sheetConfig[0])[0]).categories
                  )"
                  :key="row.key"
                  :class="row.className"
                >
                  <td :class="row.indent ? 'pl-5' : 'has-text-weight-bold'">
                    {{ row.name }}
                  </td>
                  <td
                    v-for="cell in row.cells"
                    :key="cell.key"
                    :class="cell.className + ' text-center'"
                  >
                    {{ formatCellValue(cell.value) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <CategoryListTable
              :data="data"
              :uplift-applied="isUpliftApplied"
              :uplift-type="localData.uplift_type"
              :td-without-pd="TD_WITHOUT_PD"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Original single table structure -->
    <div v-else class="table-container">
      <div>
        <h3 class="table-title">{{ tableObject[0].table_name }}</h3>
        <div class="table-responsive">
          <ReportSummaryHeader
            :data="data"
            :uplift-applied="isUpliftApplied"
            :uplift-type="localData.uplift_type"
            :td-without-pd="TD_WITHOUT_PD"
          />
          <table class="table is-striped is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th
                  :rowspan="
                    getHeaderDepth(tableObject[0].table_json.headers) + 2
                  "
                >
                  Category / Subcategory
                </th>
                <th
                  v-for="(header, headerIndex) in tableObject[0].table_json
                    .headers"
                  :key="`head0-${headerIndex}`"
                  :colspan="getHeaderColspan(header)"
                  class="text-center"
                >
                  {{ header.name }}
                </th>
              </tr>
              <tr
                v-for="level in getHeaderDepth(
                  tableObject[0].table_json.headers
                )"
                :key="`level-table0-${level}`"
              >
                <th
                  v-for="cell in buildHeaderLevel(
                    tableObject[0].table_json.headers,
                    level
                  )"
                  :key="cell.key"
                  :colspan="cell.colspan"
                  class="text-center"
                >
                  {{ cell.label }}
                </th>
              </tr>
              <tr>
                <th
                  v-for="cell in buildUnitsWeightHeaderCells(
                    tableObject[0].table_json.headers
                  )"
                  :key="cell.key"
                  class="text-center"
                >
                  {{ cell.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in buildRows(
                  tableObject[0].table_json.headers,
                  tableObject[0].table_json.categories
                )"
                :key="row.key"
                :class="row.className"
              >
                <td :class="row.indent ? 'pl-5' : 'has-text-weight-bold'">
                  {{ row.name }}
                </td>
                <td
                  v-for="cell in row.cells"
                  :key="cell.key"
                  :class="cell.className + ' text-center'"
                >
                  {{ formatCellValue(cell.value) }}
                </td>
              </tr>
            </tbody>
          </table>
          <CategoryListTable
            :data="data"
            :uplift-applied="isUpliftApplied"
            :uplift-type="localData.uplift_type"
            :td-without-pd="TD_WITHOUT_PD"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// @group Report Manager Summary
import { mapGetters } from "vuex";
import { UserGroup } from "@/services/UserGroups";
import { AdditionalReportingCategories } from "../../constants/ReportManagerConstants";
import ReportSummaryHeader from "./ReportSummaryHeader.vue";
import CategoryListTable from "./CategoryListTable.vue";
const UPLIFT_TYPES = {
  TD_WITHOUT_PD: "TD_WITHOUT_PD",
  PD_WITHOUT_WEIGHT: "PD_WITHOUT_WEIGHT"
};
export default {
  name: "ReportManagerSummary",
  components: {
    ReportSummaryHeader,
    CategoryListTable
  },
  props: {
    data: {
      type: Object
    },
    tableObject: {
      type: Array
    },
    sheetConfig: {
      type: Array,
      default: null
    },
    status: {
      type: String,
      default: ""
    },
    comparableReportList: {
      type: Array
    },
    comparedSummaryData: {
      type: Object
    }
  },
  data() {
    return {
      // Add your uplift types constant so it's accessible in the template
      TD_WITHOUT_PD: UPLIFT_TYPES.TD_WITHOUT_PD,
      PD_WITHOUT_WEIGHT: UPLIFT_TYPES.PD_WITHOUT_WEIGHT,
      currentUpliftFlag: true,
      isCompareReportModal: false,
      comparableReportDropdown: [],
      summaryDataForCompare: {},
      isCompareLastReport: false,
      isSelectReportToCompare: false,
      selectedReportToCompare: null,
      isFirstTimeApplying: true,
      // Local copy of data to avoid direct prop mutation
      localData: null,
      // Store uplift percentages separately to prevent re-rendering
      upliftValues: {},
      // Active tab for table navigation
      activeTab: 0,
      // Active sheet tab
      activeSheetTab: 0,
      // Track if initial table emission has been done
      initialTableEmitted: false
    };
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(newValue) {
        if (newValue) {
          // Create a deep copy of the data to avoid direct prop mutation
          this.localData = JSON.parse(JSON.stringify(newValue));
          if (
            this.localData.uplift_type === undefined ||
            this.localData.uplift_type === null
          ) {
            this.$set(
              this.localData,
              "uplift_type",
              UPLIFT_TYPES.TD_WITHOUT_PD
            );
          }
          // Initialize uplift values
          this.initializeUpliftValues();

          // Update the first time applying flag based on the data
          this.updateFirstTimeApplyingFlag();
        }
      }
    },
    "localData.uplift_type": {
      handler(newValue, oldValue) {
        // If the uplift type changes, reset the uplift percent
        if (newValue !== oldValue && this.localData) {
          // Always reset to 0 when changing uplift types
          this.localData.uplift_percent = 0;
        }
      }
    },
    comparableReportList(newValue) {
      this.comparableReportDropdown = newValue;
    },
    comparedSummaryData(newValue) {
      this.summaryDataForCompare = newValue;
    },
    tableObject: {
      immediate: true,
      handler(newVal) {
        if (!this.sheetConfig && newVal) {
          if (newVal.length === 1) {
            this.$emit("selectedTable", {
              tableName: newVal[0].table_name || "",
              sheetName: ""
            });
          } else if (newVal.length > 1) {
            this.$emit("selectedTable", {
              tableName: newVal[this.activeTab].table_name || "",
              sheetName: ""
            });
          }
        }
      }
    },
    sheetConfig: {
      immediate: true,
      handler(newVal) {
        // Emit initial table selection only once when sheetConfig first becomes available
        if (newVal && newVal.length > 0 && !this.initialTableEmitted) {
          this.initialTableEmitted = true;
          this.emitSelectedTable();
        }
      }
    },
    activeTab: {
      handler() {
        if (this.sheetConfig) {
          this.emitSelectedTable();
        } else if (this.tableObject && this.tableObject.length > 1) {
          const table = this.tableObject[this.activeTab];
          this.$emit("selectedTable", {
            tableName: table.table_name || "",
            sheetName: ""
          });
        }
      }
    },
    activeSheetTab: {
      handler() {
        if (this.sheetConfig) {
          // Reset table tab when sheet changes
          this.activeTab = 0;
          this.emitSelectedTable();
        }
      }
    }
  },
  mounted() {
    // Initialize localData based on props
    this.$nextTick(() => {
      if (this.data) {
        // Create a deep copy of data to avoid direct prop mutation
        this.localData = JSON.parse(JSON.stringify(this.data));

        // Set default uplift_type if null or undefined
        if (
          this.localData.uplift_type === undefined ||
          this.localData.uplift_type === null
        ) {
          this.$set(this.localData, "uplift_type", UPLIFT_TYPES.TD_WITHOUT_PD);
        }

        // Initialize uplift values
        this.initializeUpliftValues();

        // Update isFirstTimeApplying flag based on current data state
        this.updateFirstTimeApplyingFlag();
      }
    });
  },
  computed: {
    ...mapGetters(["userGroups"]),

    // Check if uplift is applied
    isUpliftApplied() {
      return (
        this.localData &&
        this.localData.uplift_applied &&
        this.localData.uplift_type
      );
    },

    isUpliftAndCompareAllowed() {
      if (
        this.userGroups.includes(UserGroup.EPR_ADMIN) ||
        this.userGroups.includes(UserGroup.REPORTING_RESPONSIBLE) ||
        this.userGroups.includes(UserGroup.CUSTOMER_ACCOUNT_MANAGER)
      ) {
        // if (this.data && this.data.uplift_applied) {
        //   return false;
        // } else {
        return true;
        //}
      } else {
        return false;
      }
    },
    checkStatus() {
      if (this.status != "Calculated") {
        return false;
      } else {
        return true;
      }
    },
    AdditionalReportingCategories: () => AdditionalReportingCategories,

    // Determine which data structure to use
    useSheetConfig() {
      return this.sheetConfig && this.sheetConfig.length > 0;
    },

    // Get current sheet
    currentSheet() {
      if (this.useSheetConfig) {
        return this.sheetConfig[this.activeSheetTab];
      }
      return null;
    },

    // Get tables for current sheet
    currentTables() {
      if (this.useSheetConfig) {
        const sheet = this.currentSheet;
        return sheet?.sheet_json?.tables || [];
      }
      return this.tableObject || [];
    }
  },
  methods: {
    emitSelectedTable() {
      if (this.sheetConfig && this.sheetConfig.length > 0) {
        const sheet = this.sheetConfig[this.activeSheetTab];
        const tables = this.getSheetTables(sheet);
        if (tables.length > 0) {
          const selectedTable = tables[this.activeTab];
          const tableName =
            selectedTable?.table_name ||
            selectedTable?.table_json?.tableName ||
            "";
          const sheetName =
            sheet?.sheet_name || sheet?.name || sheet?.sheet_json?.name || "";
          this.$emit("selectedTable", {
            tableName: tableName,
            sheetName: sheetName
          });
        }
      }
    },
    nameWithLang(value) {
      return `${value.report_tracker__period} — (${value.status})`;
    },
    // Helper method to get tables from a sheet
    getSheetTables(sheet) {
      if (!sheet) return [];
      return sheet.tables || sheet.sheet_json?.tables || [];
    },
    // Helper method to get table data (handles both table_json and nested structures)
    getTableData(table) {
      if (!table) return { headers: [], categories: [] };
      if (table.table_json) {
        return table.table_json;
      }
      return table;
    },
    compareWithSelected() {
      this.isSelectReportToCompare = false;
      this.loadCompareReport(this.selectedReportToCompare.id);
      this.isCompareReportModal = true;
    },
    cancelCompareWithSelected() {
      this.isSelectReportToCompare = false;
    },
    exportReport() {
      this.$emit("exportCompareReport", this.selectedReportToCompare.id);
    },
    selectReportToCompare() {
      this.loadCompareReportList();
      this.isSelectReportToCompare = true;
    },
    closeModal() {
      this.isCompareLastReport = false;
      this.isSelectReportToCompare = true;
      this.isCompareReportModal = false;
    },
    loadCompareReport(id) {
      this.$emit("loadCompareReport", id);
    },
    loadCompareReportList() {
      this.$emit("loadCompareReportList");
    },
    showApplyUplift(value) {
      this.currentUpliftFlag = value;
      // Emit event to update parent
      this.$emit("update:data", this.localData);
    },
    changeApplyUplift() {
      if (this.localData) {
        // Reset uplift percent for both types
        this.localData.uplift_percent = 0;

        // Reset all uplift values when switching uplift types
        this.upliftValues = {};

        // If we have category data, reset uplift_percent values to 0
        if (
          this.localData.category_list &&
          this.localData.category_list.length > 0
        ) {
          this.localData.category_list.forEach(item => {
            // Reset main category uplift percent
            if (item.id) {
              item.uplift_percent = 0;
              this.upliftValues[`cat_${item.id}`] = 0;
            }

            // Reset sub-categories uplift percent
            if (item.sub_categories && item.sub_categories.length > 0) {
              item.sub_categories.forEach(subItem => {
                if (subItem.id) {
                  subItem.uplift_percent = 0;
                  this.upliftValues[`subcat_${subItem.id}`] = 0;
                }
              });
            }
          });
        }

        // Update the Apply button visibility based on the selected uplift type
        if (this.localData.uplift_type === this.TD_WITHOUT_PD) {
          this.isFirstTimeApplying = true;
        } else if (this.localData.uplift_type === this.PD_WITHOUT_WEIGHT) {
          this.updateFirstTimeApplyingFlag();
        }

        // Emit event to update parent
        this.$emit("update:data", this.localData);
      }
    },

    // Helper method to update isFirstTimeApplying flag based on current data state
    updateFirstTimeApplyingFlag() {
      // Safeguard against incomplete data
      if (!this.localData) {
        return;
      }

      // If uplift is not applied, reset flag
      if (!this.localData.uplift_applied) {
        this.isFirstTimeApplying = true;
        return;
      }

      // If uplift type is TD_WITHOUT_PD, always show Apply button
      if (this.localData.uplift_type === this.TD_WITHOUT_PD) {
        this.isFirstTimeApplying = true;
        return;
      }

      // From here on, we're dealing with PD_WITHOUT_WEIGHT uplift type

      // Check if there are categories
      const hasCategories =
        this.localData.category_list && this.localData.category_list.length > 0;

      // If there are categories, always enable Apply button for PD_WITHOUT_WEIGHT
      if (hasCategories) {
        this.isFirstTimeApplying = true;
        return;
      }

      // Check for evidence of previously applied uplift
      const hasUplifted =
        (this.localData.sum_uplifted_weight &&
          this.localData.sum_uplifted_weight > 0) ||
        (this.localData.sum_final_uplifted_fee &&
          this.localData.sum_final_uplifted_fee > 0);

      // Update flag based on whether uplift has been applied before
      this.isFirstTimeApplying = !hasUplifted;
    },
    // Method to sync uplift values from our storage to the actual objects
    syncUpliftValues() {
      if (!this.localData || !this.localData.category_list) return;

      // Sync values from upliftValues back to category objects
      this.localData.category_list.forEach(item => {
        if (item.id && this.upliftValues[`cat_${item.id}`] !== undefined) {
          item.uplift_percent = this.upliftValues[`cat_${item.id}`];
        }

        // Process subcategories if any
        if (item.sub_categories && item.sub_categories.length > 0) {
          item.sub_categories.forEach(subItem => {
            if (
              subItem.id &&
              this.upliftValues[`subcat_${subItem.id}`] !== undefined
            ) {
              subItem.uplift_percent = this.upliftValues[
                `subcat_${subItem.id}`
              ];
            }
          });
        }
      });
    },

    applyUpliftBtnAction() {
      if (!this.localData) return;

      // Sync uplift values back to objects
      this.syncUpliftValues();

      // Emit event to update parent with localData
      this.$emit("upliftConfirm", this.localData);

      // If it's PD_WITHOUT_WEIGHT and there are no categories, hide the Apply button after applying
      if (this.localData.uplift_type === this.PD_WITHOUT_WEIGHT) {
        const hasCategories =
          this.localData.category_list &&
          this.localData.category_list.length > 0;

        // Only hide the Apply button if there are no categories
        // This ensures it remains visible when there are categories
        if (!hasCategories) {
          this.isFirstTimeApplying = false;
        }
      }
    },
    revertBtnAction() {
      this.$buefy.dialog.confirm({
        title: "Confirmation",
        confirmText: "Yes",
        message: "Do you want to revert applied uplift ?",
        trapFocus: true,
        onConfirm: () => {
          // Reset the local data uplift_applied flag
          if (this.localData) {
            this.localData.uplift_applied = false;
            this.localData.uplift_type = null;
            this.localData.uplift_percent = 0;

            // Reset isFirstTimeApplying flag
            this.isFirstTimeApplying = true;

            // Reset uplift values storage
            this.upliftValues = {};

            // Emit event to update parent
            this.$emit("update:data", this.localData);
          }

          // Also emit the original revert event for any backend operations
          this.$emit("revertUplift");
        }
      });
    },

    // Method to initialize uplift values storage
    initializeUpliftValues() {
      if (!this.localData || !this.localData.category_list) return;

      // Reset the storage
      this.upliftValues = {};

      // Process each category
      this.localData.category_list.forEach(item => {
        if (item.id) {
          // Store main category uplift value
          this.upliftValues[`cat_${item.id}`] = item.uplift_percent;

          // Process subcategories if any
          if (item.sub_categories && item.sub_categories.length > 0) {
            item.sub_categories.forEach(subItem => {
              if (subItem.id) {
                this.upliftValues[`subcat_${subItem.id}`] =
                  subItem.uplift_percent;
              }
            });
          }
        }
      });
    },

    // Debounce function to limit update frequency
    debounce(func, wait) {
      let timeout;
      return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    },

    // Method to update sub-item uplift percent without causing full re-render
    updateSubItemUpliftPercent: function(value, subItem) {
      // Use a method property to ensure debounce has correct context
      if (!this._debouncedSubItemUpdate) {
        this._debouncedSubItemUpdate = this.debounce(function(value, subItem) {
          if (!subItem || !subItem.id) return;

          // Store the value in our separate storage ONLY
          // Don't modify the actual object until apply is clicked
          this.upliftValues[`subcat_${subItem.id}`] = value;
        }, 300); // 300ms debounce
      }

      this._debouncedSubItemUpdate(value, subItem);
    },

    // Method to update main item uplift percent without causing full re-render
    updateItemUpliftPercent: function(value, item) {
      // Use a method property to ensure debounce has correct context
      if (!this._debouncedItemUpdate) {
        this._debouncedItemUpdate = this.debounce(function(value, item) {
          if (!item || !item.id) return;

          // Store the value in our separate storage ONLY
          // Don't modify the actual object until apply is clicked
          this.upliftValues[`cat_${item.id}`] = value;
        }, 300); // 300ms debounce
      }

      this._debouncedItemUpdate(value, item);
    },

    // Helper method to get uplift percent value
    getItemUpliftPercent(item) {
      if (!item || !item.id) return 0;
      return this.upliftValues[`cat_${item.id}`] !== undefined
        ? this.upliftValues[`cat_${item.id}`]
        : item.uplift_percent;
    },

    // Helper method to get sub-item uplift percent value
    getSubItemUpliftPercent(subItem) {
      if (!subItem || !subItem.id) return 0;
      return this.upliftValues[`subcat_${subItem.id}`] !== undefined
        ? this.upliftValues[`subcat_${subItem.id}`]
        : subItem.uplift_percent;
    },

    // Method to update the global uplift percentage
    updateGlobalUpliftPercent(value) {
      if (this.localData) {
        // Store the value without triggering reactivity issues
        this.localData.uplift_percent = value;
      }
    },

    // Helper method to format numbers with commas
    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    // Helper method to format cell values: show empty string for 0, round to 3 decimals
    formatCellValue(value) {
      if (value === 0) return "";
      if (typeof value === "number") {
        return parseFloat(value.toFixed(3));
      }
      return value;
    },

    // Header helpers for dynamic subheaders
    hasAnySubHeaders(headers) {
      return (
        Array.isArray(headers) &&
        headers.some(
          h => Array.isArray(h.subHeaders) && h.subHeaders.length > 0
        )
      );
    },
    // Calculate the maximum depth of nested headers
    getHeaderDepth(headers) {
      if (!headers || !Array.isArray(headers)) return 0;

      const calculateDepth = headerList => {
        let depth = 0;
        for (const header of headerList) {
          if (
            Array.isArray(header.subHeaders) &&
            header.subHeaders.length > 0
          ) {
            depth = Math.max(depth, 1 + calculateDepth(header.subHeaders));
          }
        }
        return depth;
      };

      return calculateDepth(headers);
    },
    // Build header cells for a specific level
    buildHeaderLevel(headers, level) {
      if (!headers || !Array.isArray(headers)) return [];

      const cells = [];

      const addCellsForHeader = (header, currentLevel, path = []) => {
        const headerPath = [...path, header.name || "header"];
        if (currentLevel === level) {
          if (
            Array.isArray(header.subHeaders) &&
            header.subHeaders.length > 0
          ) {
            header.subHeaders.forEach((sub, subIndex) => {
              const subColspan = this.getHeaderColspan(sub);
              cells.push({
                key: `${headerPath.join("-")}-sub-${subIndex}`,
                label: sub.name,
                colspan: subColspan
              });
            });
          }
          // For leaf headers, don't add any cells in subheader rows
        } else if (currentLevel < level) {
          if (
            Array.isArray(header.subHeaders) &&
            header.subHeaders.length > 0
          ) {
            header.subHeaders.forEach((sub, subIndex) => {
              addCellsForHeader(sub, currentLevel + 1, [
                ...headerPath,
                `sub-${subIndex}`
              ]);
            });
          }
          // For leaf headers, don't add placeholders
        }
      };

      headers.forEach((header, index) => {
        addCellsForHeader(header, 1, [`h-${index}`]);
      });

      return cells;
    },
    getHeaderColspan(header) {
      if (!header) return 2;

      // Recursively count leaf nodes
      const countLeaves = h => {
        if (Array.isArray(h.subHeaders) && h.subHeaders.length > 0) {
          return h.subHeaders.reduce((sum, sub) => sum + countLeaves(sub), 0);
        }
        return 1; // This is a leaf
      };

      const leafCount = countLeaves(header);
      return leafCount * 2; // each leaf column has Units + Weight
    },
    // Flatten headers to an ordered list of leaf headers to align data cells correctly
    flattenHeaders(headers) {
      if (!headers) return [];
      const leaves = [];

      const flattenRecursive = (
        headerList,
        parentUnitsKey = "total_units",
        parentWeightKey = "total_weight",
        path = []
      ) => {
        headerList.forEach(header => {
          const headerUnitsKey = header.units_key || parentUnitsKey;
          const headerWeightKey = header.weight_key || parentWeightKey;
          // Trim whitespace from header names for path
          const trimmedName =
            typeof header.name === "string" ? header.name.trim() : header.name;
          const currentPath = [...path, trimmedName];

          if (
            Array.isArray(header.subHeaders) &&
            header.subHeaders.length > 0
          ) {
            // Recursively flatten subheaders
            flattenRecursive(
              header.subHeaders,
              headerUnitsKey,
              headerWeightKey,
              currentPath
            );
          } else {
            // Leaf node - store the full path to this leaf
            leaves.push({
              name: trimmedName,
              unitsKey: headerUnitsKey,
              weightKey: headerWeightKey,
              path: currentPath // Full path from root to leaf
            });
          }
        });
      };

      flattenRecursive(headers);
      return leaves;
    },
    pickNumber(...values) {
      for (let i = 0; i < values.length; i += 1) {
        if (values[i] !== undefined && values[i] !== null) {
          return values[i];
        }
      }
      return 0;
    },
    buildSubHeaderNames(headers) {
      if (!headers) return [];
      const cells = [];
      headers.forEach((h, hIdx) => {
        if (Array.isArray(h.subHeaders) && h.subHeaders.length > 0) {
          h.subHeaders.forEach((s, sIdx) => {
            cells.push({ key: `h-${hIdx}-sub-${sIdx}`, label: s.name });
          });
        } else {
          // No subheaders: create a placeholder spanning 2 cols under the header
          cells.push({ key: `h-${hIdx}-sub-0`, label: "" });
        }
      });
      return cells;
    },
    buildUnitsWeightHeaderCells(headers) {
      if (!headers) return [];
      const cells = [];
      const leafHeaders = this.flattenHeaders(headers);

      leafHeaders.forEach((leaf, leafIdx) => {
        cells.push({
          key: `leaf-${leafIdx}-units`,
          label: "Total Units"
        });
        cells.push({
          key: `leaf-${leafIdx}-weight`,
          label: "Total Weight"
        });
      });

      return cells;
    },

    buildCategoryCells(headers, category, catIndex) {
      if (!headers) return [];
      const cells = [];
      const leafHeaders = this.flattenHeaders(headers);
      const summaries = category.header_summaries || [];

      leafHeaders.forEach((leaf, leafIdx) => {
        const summary = this.findSummaryForLeaf(leaf, summaries, leafIdx);

        cells.push({
          key: `cat-${catIndex}-leaf-${leafIdx}-units`,
          className: "has-background-light",
          value: this.pickNumber(
            summary[leaf.unitsKey],
            summary.total_units,
            category[leaf.unitsKey],
            category.total_units
          )
        });
        cells.push({
          key: `cat-${catIndex}-leaf-${leafIdx}-weight`,
          className: "has-background-light",
          value: this.pickNumber(
            summary[leaf.weightKey],
            summary.total_weight,
            category[leaf.weightKey],
            category.total_weight
          )
        });
      });
      return cells;
    },

    buildSubCategoryCells(headers, subCategory, catIndex, subCatIndex) {
      if (!headers) return [];
      const cells = [];
      const leafHeaders = this.flattenHeaders(headers);
      const summaries = subCategory.header_summaries || [];

      leafHeaders.forEach((leaf, leafIdx) => {
        const summary = this.findSummaryForLeaf(leaf, summaries, leafIdx);

        cells.push({
          key: `subcat-${catIndex}-${subCatIndex}-leaf-${leafIdx}-units`,
          value: this.pickNumber(
            summary[leaf.unitsKey],
            summary.total_units,
            subCategory[leaf.unitsKey],
            subCategory.total_units
          )
        });
        cells.push({
          key: `subcat-${catIndex}-${subCatIndex}-leaf-${leafIdx}-weight`,
          value: this.pickNumber(
            summary[leaf.weightKey],
            summary.total_weight,
            subCategory[leaf.weightKey],
            subCategory.total_weight
          )
        });
      });
      return cells;
    },

    buildRows(headers, categories) {
      if (!headers || !categories) return [];
      const rows = [];
      categories.forEach((category, catIndex) => {
        rows.push({
          key: `cat-${catIndex}`,
          className: "category-row",
          indent: false,
          name: category.name,
          cells: this.buildCategoryCells(headers, category, catIndex)
        });

        if (category.sub_categories && category.sub_categories.length > 0) {
          category.sub_categories.forEach((subCategory, subCatIndex) => {
            rows.push({
              key: `subcat-${catIndex}-${subCatIndex}`,
              className: "subcategory-row",
              indent: true,
              name: subCategory.name,
              cells: this.buildSubCategoryCells(
                headers,
                subCategory,
                catIndex,
                subCatIndex
              )
            });
          });
        }
      });
      return rows;
    },

    findSummaryForLeaf(leaf, summaries, fallbackIdx) {
      if (!Array.isArray(summaries)) return {};

      // New hierarchical structure: traverse the nested summaries based on the leaf's path
      if (leaf.path && leaf.path.length > 0) {
        return this.findSummaryByPath(leaf.path, summaries);
      }

      // Fallback to old logic for backward compatibility
      const match = summaries.find(summary => {
        return (
          summary?.units_key === leaf.unitsKey ||
          summary?.weight_key === leaf.weightKey ||
          summary?.name === leaf.name ||
          summary?.header_name === leaf.name
        );
      });

      return match || summaries[fallbackIdx] || {};
    },

    // Traverse the nested header_summaries structure to find the matching summary
    findSummaryByPath(path, summaries) {
      if (!path || path.length === 0) return {};
      if (!Array.isArray(summaries)) return {};

      // Start from the first level
      let currentSummaries = summaries;
      let currentSummary = null;

      // Traverse each level of the path
      for (let i = 0; i < path.length; i++) {
        // Always trim whitespace for matching
        const headerName =
          typeof path[i] === "string" ? path[i].trim() : path[i];

        // Find the summary matching this header name (trim summary too)
        currentSummary = currentSummaries.find(
          s =>
            typeof s.header_name === "string" &&
            s.header_name.trim() === headerName
        );

        if (!currentSummary) {
          // No match found at this level
          return {};
        }

        // If this is not the last level and summary has children, go deeper
        if (
          i < path.length - 1 &&
          Array.isArray(currentSummary.children) &&
          currentSummary.children.length > 0
        ) {
          currentSummaries = currentSummary.children;
        } else if (i === path.length - 1) {
          // We've reached the leaf level
          return currentSummary;
        } else {
          // Path continues but no children available
          return {};
        }
      }

      return currentSummary || {};
    }
  }
};
</script>

<style scoped lang="scss">
.table-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0rem;
  color: #363636;
  padding-bottom: 0.5rem !important;
}

.table-responsive {
  overflow-x: auto;
}

.table {
  border-collapse: collapse;
  background: white;
}

.table thead th {
  background-color: #f5f5f5;
  border-bottom: 2px solid #dbdbdb;
  border-right: 1px solid #dbdbdb;
  padding: 0.75rem;
  font-weight: 600;
  vertical-align: top;
}
.text-center {
  text-align: center !important;
}

.table thead th:last-child {
  border-left: none;
}

.table tbody td {
  padding: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.category-row {
  background-color: #fafafa;
}

.category-row td {
  border-top: 2px solid #dbdbdb;
  border-bottom: 2px solid #dbdbdb;
}

.subcategory-row td {
  border-left: 3px solid #e8e8e8;
}

.subcategory-row:last-child td {
  border-bottom: 2px solid #dbdbdb;
}

.pl-5 {
  padding-left: 2rem !important;
}

.tag {
  font-size: 0.75rem;
  white-space: nowrap;
}

.sheet-tabs /deep/ .tabs,
.table-tabs /deep/ .tabs {
  margin: 0 !important;
}

.table-tabs .tabs ul {
  border-bottom: 2px solid #dbdbdb;
}

/deep/ .tab-content {
  padding: 0 !important;
  margin: 0 !important;
}

.sheet-tabs .tabs li.is-active a {
  background-color: #3273dc;
  color: white;
  border-color: #3273dc;
}

.sheet-tabs .tab-content {
  padding: 1rem 0 !important;
}
</style>
